import axios from "axios";

export const getInvoices = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get("/invoices", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getInvoice = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/invoices/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createInvoice = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/invoices/create-invoice`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Staff override: grant the payment discount outside the automatic
// eligibility rules (minimum total, 5-day window). The backend also
// syncs it to Zoho. Returns the updated invoice, or { message } on refusal.
export const applyInvoicePaymentDiscount = async (id: string) => {
	try {
		const res: any = await axios.post(`/invoices/${id}/apply-payment-discount`, {});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const emailInvoice = async ({ data }: any) => {
	try {
		// Session 28 hotfix 6 (Phase 3): backend reads
		// invoice.mulchMapSnapshotIds directly. No opt-in flag anymore.
		const res: any = await axios.post(`/invoices/${data.id}/send-by-email`, {});
		// Session 65 item 6a: the backend returns `{ success: true }` for BOTH
		// a real send AND a plan-suppressed send (message: 'Invoice email
		// suppressed by plan notification settings.'). Callers must branch on
		// the explicit `success` flag, not on the presence of a `message`.
		// Preserve the backend's own success/message (spread after the
		// default) and mark a thrown request as an explicit failure.
		return { success: true, ...(res?.data ?? {}) };
	} catch (e: any) {
		const body = e?.response?.data ?? {};
		return { success: false, ...(typeof body === "object" ? body : { message: String(body) }) };
	}
};

export const updateInvoice = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/invoices/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getInvoicePayments = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/invoices/${data?.id}/payments`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createPayment = async ({ id, data }: any) => {
	try {
		const res: any = await axios.post(`/invoices/${id}/payments`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getInvoicePDF = async ({ data }: any) => {	
	try {
		// Session 28 hotfix 6 (Phase 3): backend reads
		// invoice.mulchMapSnapshotIds directly.
		const res: any = await axios.get(`/invoices/${data?.id}/pdf`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const deletePayment = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(`/invoices/${data?.id}/payments/${data?.paymentId}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updatePayment = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/invoices/${data?.id}/payments/${data?.paymentId}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createPaymentIntent = async ({ id, paymentType }: any) => {
	try {
		const res: any = await axios.post(`/invoices/${id}/create-payment-intent`, { paymentType }); 
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Maintenance Module Phase 1 §3.9 #18 (mockup 22, Session 40) —
// Corrigo Invoice submission panel data. Staff-only, read-only.
// Mirrors api/Quotes.getCorrigoExport. See
// js_crm_backend src/modules/corrigo-export/services/corrigo-export.service.ts
// (getInvoiceExport) for the category-mapping + WO# resolution logic.
export const getCorrigoExport = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/invoices/${data?.id}/corrigo-export`);
		return res?.data ? res.data : { invoiceId: data?.id, lines: [], planId: null, planName: null, posted: null };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { invoiceId: data?.id, lines: [], planId: null, planName: null, posted: null };
	}
};

// Maintenance Module Phase 1 §3.9 #18 — audit-only "Mark posted" action.
// No externalWorkOrderNumber input; the backend resolves it from the
// WO chain automatically. `note` is optional.
export const markPostedToCorrigo = async ({ data, note }: { data: { id: string }; note?: string }) => {
	try {
		const res: any = await axios.post(`/invoices/${data.id}/corrigo-export/mark-posted`, { note });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Session 65 item 8 - read/save the invoice Corrigo panel's pre-posting
// checklist progress so it survives a refresh. Mirrors api/Quotes.
export const getCorrigoProgress = async ({ data }: { data: { id: string } }) => {
	try {
		const res: any = await axios.get(`/invoices/${data?.id}/corrigo-export/progress`);
		return res?.data ? res.data : { success: false, progress: null };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false, progress: null };
	}
};

export const saveCorrigoProgress = async ({ data }: { data: { id: string; progress: any } }) => {
	try {
		const res: any = await axios.patch(`/invoices/${data.id}/corrigo-export/progress`, { progress: data.progress });
		return res?.data ? res.data : { success: false };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false };
	}
};